/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 *
 * Every screen goes through this file. No feature calls `fetch` or the
 * @hey-api client directly: the SDK is generated from the backend's OpenAPI
 * schema, so a route that changes shape breaks the type-check rather than the
 * running page.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { OffboardingStatus } from "@/lib/api/domain";
import {
  type AccessChangeRequest,
  type AdhocRequest,
  applyOrgScenario,
  approveOrgScenario,
  approveWorkforceChange,
  archiveOrgScenario,
  cancelOffboarding,
  type ChangeRequest,
  confirmOrgAdhoc,
  createBlankOrgScenario,
  createOffboarding,
  createOrgAdhoc,
  createOrgScenario,
  createRiskFlag,
  deleteOrgChange,
  deleteOrgScenario,
  type EmployeeChangeIn,
  expireRiskFlag,
  type FutureHireIn,
  getAdminStatus,
  getHealth,
  getMe,
  getOffboarding,
  getOrgAdhoc,
  getOrgScenario,
  getOrgSnapshot,
  getWorkforceChange,
  getWorkforceLog,
  getWorkforceVocab,
  type LeaverCreateRequest,
  listAccessGroups,
  listFutureHires,
  listOffboardings,
  listOrgScenarios,
  listRiskFlags,
  listWorkforceChanges,
  logout,
  lookUpDirectoryUser,
  type PlannedExitIn,
  previewOrgImport,
  putOrgChange,
  refreshOrgSnapshot,
  reinstateOffboarding,
  reopenOrgScenario,
  requestAccessAdd,
  requestAccessRemove,
  restoreOrgScenario,
  retryAccessRequest,
  retryOrgScenario,
  returnWorkforceChange,
  revertOffboarding,
  type RiskFlagCreateRequest,
  searchDirectoryUsers,
  submitEmployeeChange,
  submitFutureHire,
  submitPlannedExit,
  testOrgScenario,
  undoOrgScenario,
  updateEmployeeChange,
  updateFutureHire,
  updateOrgScenario,
  updatePlannedExit,
  withdrawWorkforceChange,
  type WorkforceRecord,
} from "@/lib/api/generated";

// ─── Query keys ──────────────────────────────────────────────────────────
// Kept in one place so a mutation can invalidate exactly what it makes stale.
// `["offboarding"]` is the prefix for both the queue list and a single record,
// so invalidating it refreshes every view of an offboarding.

export const queryKeys = {
  offboardings: (status?: OffboardingStatus | null) =>
    ["offboarding", "list", status ?? "all"] as const,
  offboarding: (id: string) => ["offboarding", "detail", id] as const,
  riskFlags: (includeExpired: boolean) => ["risk-flags", { includeExpired }] as const,
  accessGroups: ["access"] as const,
  orgSnapshot: ["org", "snapshot"] as const,
  orgScenarios: ["org", "scenarios"] as const,
  orgScenario: (id: string) => ["org", "scenario", id] as const,
  workforce: ["workforce"] as const,
  workforceChange: (id: string) => ["workforce", "detail", id] as const,
};

// ─── Session ─────────────────────────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

/**
 * The operations view. Admin only, and a 403 here is a correct answer rather
 * than an error, so it is not retried.
 */
export function useAdminStatus(enabled: boolean) {
  return useQuery({
    queryKey: ["admin-status"],
    enabled,
    queryFn: async () => {
      const { data } = await getAdminStatus();
      return data;
    },
    retry: false,
    // Short, because the point of the page is "right now".
    staleTime: 15 * 1000,
  });
}

/**
 * Who this UPN actually is, asked as HR finishes typing it.
 *
 * `enabled` gates on a plausible address rather than on every keystroke: this
 * is a directory read, and firing one per character would be both wasteful and
 * a small stream of half-typed names in the audit trail of whoever reads Graph
 * logs. `retry: false` because the two failure modes are already meaningful —
 * 404 is "no such person", 503 is "the directory could not be read" — and
 * retrying blurs them into a spinner.
 */
export function useDirectoryLookup(upn: string) {
  const trimmed = upn.trim();
  return useQuery({
    queryKey: ["directory", trimmed.toLowerCase()],
    enabled: /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed),
    queryFn: async () => {
      const { data } = await lookUpDirectoryUser({ query: { upn: trimmed } });
      return data;
    },
    retry: false,
    staleTime: 60 * 1000,
  });
}

/**
 * People matching what HR has typed so far, for the picker.
 *
 * Gated on two characters (the API refuses fewer anyway) and on the text NOT
 * already being a complete address: once somebody has pasted a full UPN there
 * is nothing left to pick, and a dropdown covering the field at that moment is
 * in the way rather than helpful.
 */
export function useDirectorySearch(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ["directory-search", trimmed.toLowerCase()],
    enabled: enabled && trimmed.length >= 2 && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed),
    queryFn: async () => {
      const { data } = await searchDirectoryUsers({ query: { q: trimmed } });
      return data;
    },
    retry: false,
    staleTime: 60 * 1000,
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Offboardings ────────────────────────────────────────────────────────

/** The queue table. `status` narrows it; null or undefined means everything. */
export function useOffboardings(status?: OffboardingStatus | null) {
  return useQuery({
    queryKey: queryKeys.offboardings(status),
    queryFn: async () => {
      const { data } = await listOffboardings({
        query: status ? { status: [status] } : undefined,
      });
      return data;
    },
  });
}

/** One record with its snapshot, its action log and the subject's risk flags. */
export function useOffboarding(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.offboarding(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data } = await getOffboarding({ path: { offboarding_id: id as string } });
      return data;
    },
    retry: false,
  });
}

/**
 * Write a leaver record.
 *
 * This creates a row at status `scheduled` and nothing else. When and whether
 * it is acted on is the worker's business, reported on /admin.
 */
export function useCreateOffboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: LeaverCreateRequest) => {
      const { data } = await createOffboarding({ body });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offboarding"] });
    },
  });
}

/**
 * Revert, cancel, or reinstate one record. All three write an append-only
 * action_log row; only reinstate ever reaches the tenant, and even then not
 * from here — it queues the worker.
 */
export function useOffboardingAction(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      action,
      reason,
    }: {
      action: "revert" | "cancel" | "reinstate";
      reason: string;
    }) => {
      const call =
        action === "revert"
          ? revertOffboarding
          : action === "cancel"
            ? cancelOffboarding
            : reinstateOffboarding;
      const { data } = await call({ path: { offboarding_id: id }, body: { reason } });
      return data;
    },
    onSuccess: (result) => {
      if (result) {
        queryClient.setQueryData(queryKeys.offboarding(id), result.offboarding);
      }
      queryClient.invalidateQueries({ queryKey: ["offboarding"] });
    },
  });
}

// ─── Risk flags ──────────────────────────────────────────────────────────

export function useRiskFlags(includeExpired: boolean) {
  return useQuery({
    queryKey: queryKeys.riskFlags(includeExpired),
    queryFn: async () => {
      const { data } = await listRiskFlags({ query: { include_expired: includeExpired } });
      return data;
    },
  });
}

export function useCreateRiskFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RiskFlagCreateRequest) => {
      const { data } = await createRiskFlag({ body });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["risk-flags"] });
    },
  });
}

/** Retire a flag before its window ends. A flag is never deleted. */
export function useExpireRiskFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ flagId, note }: { flagId: string; note?: string | null }) => {
      const { data } = await expireRiskFlag({ path: { flag_id: flagId }, body: { note } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["risk-flags"] });
    },
  });
}

// ─── Access lists ────────────────────────────────────────────────────────
// The groups HR maintains. The list comes from the server's configuration;
// nothing on this side ever names a group the server did not offer.

export function useAccessGroups(enabled = true) {
  return useQuery({
    queryKey: queryKeys.accessGroups,
    queryFn: async () => {
      const { data } = await listAccessGroups();
      return data;
    },
    enabled,
    // A pending request turns into an applied one on the worker's next pass;
    // polling keeps the page honest without a reload.
    refetchInterval: 30_000,
  });
}

export function useRequestAccessAdd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, body }: { groupId: string; body: AccessChangeRequest }) => {
      const { data } = await requestAccessAdd({ path: { group_id: groupId }, body });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessGroups });
    },
  });
}

export function useRequestAccessRemove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, body }: { groupId: string; body: AccessChangeRequest }) => {
      const { data } = await requestAccessRemove({ path: { group_id: groupId }, body });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessGroups });
    },
  });
}

export function useRetryAccessRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data } = await retryAccessRequest({ path: { request_id: requestId } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessGroups });
    },
  });
}

// ─── Org chart ───────────────────────────────────────────────────────────
// Visible to the HROPS_ORGCHART_UPNS allow-list only; every route answers 404
// to anyone else, so these hooks are gated on `org_chart_enabled`.

export function useOrgSnapshot(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.orgSnapshot,
    queryFn: async () => {
      const { data } = await getOrgSnapshot();
      return data;
    },
    enabled,
    // ~2 500 people, one fetch; nothing about it changes between refreshes.
    staleTime: 10 * 60 * 1000,
  });
}

export function useRefreshOrgSnapshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await refreshOrgSnapshot();
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

/** Upload a workbook and get the verdict for every row. Writes nothing. */
export function usePreviewOrgImport() {
  return useMutation({
    mutationFn: async ({ file, picks }: { file: File; picks: Record<string, OrgPick> }) => {
      const { data } = await previewOrgImport({ body: { file, picks: JSON.stringify(picks) } });
      return data;
    },
  });
}

export interface OrgPick {
  person_id?: string;
  /** "" means: leave the manager line as it is. */
  manager_id?: string;
}

export function useCreateOrgScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      file: File;
      name: string;
      description?: string;
      picks: Record<string, OrgPick>;
    }) => {
      const { data } = await createOrgScenario({
        body: { ...body, picks: JSON.stringify(body.picks) },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgScenarios });
    },
  });
}

/** A scenario with no file, built by hand afterwards. */
export function useCreateBlankOrgScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await createBlankOrgScenario({ body: { name } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orgScenarios });
    },
  });
}

export function useOrgScenarios(
  enabled: boolean,
  includeArchived = false,
  kind: "scenario" | "adhoc" | "all" = "scenario",
) {
  return useQuery({
    queryKey: [...queryKeys.orgScenarios, includeArchived, kind],
    queryFn: async () => {
      const { data } = await listOrgScenarios({
        query: { include_archived: includeArchived, kind },
      });
      return data;
    },
    enabled,
  });
}

/** Prepare an ad-hoc change. Writes nothing — it returns what would be written. */
export function useCreateOrgAdhoc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdhocRequest) => {
      const { data } = await createOrgAdhoc({ body });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

/** The confirmation that writes. `confirm` is always true here; the server
 *  refuses anything else, and the dialog is what makes it a decision. */
export function useConfirmOrgAdhoc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await confirmOrgAdhoc({
        path: { scenario_id: id },
        body: { confirm: true },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

/**
 * Prepare the reversal of a change that reached AD.
 *
 * Writes nothing: it returns a new draft of the same kind, which is then
 * confirmed (ad-hoc) or approved and applied (scenario) like any other.
 */
export function useUndoOrgScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await undoOrgScenario({
        path: { scenario_id: id },
        body: { reason },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

export function useOrgAdhoc(id: string | null) {
  return useQuery({
    queryKey: ["org", "adhoc", id ?? ""],
    queryFn: async () => {
      const { data } = await getOrgAdhoc({ path: { scenario_id: id! } });
      return data;
    },
    enabled: !!id,
    // Same reason as a scenario: the worker needs a pass, and Entra needs the
    // sync cycle, before "applied" is true.
    refetchInterval: (query) => {
      const st = query.state.data?.scenario.status;
      return st === "applying" ? 20_000 : false;
    },
  });
}

export function useOrgScenario(id: string | null) {
  return useQuery({
    queryKey: queryKeys.orgScenario(id ?? ""),
    queryFn: async () => {
      const { data } = await getOrgScenario({ path: { scenario_id: id! } });
      return data;
    },
    enabled: !!id,
    // The worker picks up a test or an apply within five minutes, and a
    // written change shows in Entra after the sync cycle: keep the page live
    // while either is in flight.
    refetchInterval: (query) => {
      const st = query.state.data?.scenario.status;
      return st === "testing" || st === "applying" ? 20_000 : false;
    },
  });
}

type ScenarioAction =
  | "archive"
  | "restore"
  | "approve"
  | "reopen"
  | "test"
  | "apply"
  | "retry"
  | "delete";

const SCENARIO_ACTIONS: Record<ScenarioAction, (id: string) => Promise<unknown>> = {
  archive: (id) => archiveOrgScenario({ path: { scenario_id: id } }),
  restore: (id) => restoreOrgScenario({ path: { scenario_id: id } }),
  approve: (id) => approveOrgScenario({ path: { scenario_id: id } }),
  reopen: (id) => reopenOrgScenario({ path: { scenario_id: id } }),
  test: (id) => testOrgScenario({ path: { scenario_id: id } }),
  apply: (id) => applyOrgScenario({ path: { scenario_id: id } }),
  retry: (id) => retryOrgScenario({ path: { scenario_id: id } }),
  delete: (id) => deleteOrgScenario({ path: { scenario_id: id } }),
};

/** Every lifecycle button: one POST (or the DELETE), then refresh everything org. */
export function useOrgScenarioAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: ScenarioAction }) => {
      await SCENARIO_ACTIONS[action](id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

export function useRenameOrgScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data } = await updateOrgScenario({ path: { scenario_id: id }, body: { name } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

export function usePutOrgChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ChangeRequest }) => {
      const { data } = await putOrgChange({ path: { scenario_id: id }, body });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

export function useDeleteOrgChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, changeId }: { id: string; changeId: string }) => {
      await deleteOrgChange({ path: { scenario_id: id, change_id: changeId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

// ─── Workforce changes ───────────────────────────────────────────────────
// Behind the HROps.Workforce role: every hook takes `enabled` from
// `me.workforce_enabled`, so a user without the role never fires a 403.

export function useWorkforceVocab(enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.workforce, "vocab"],
    queryFn: async () => {
      const { data } = await getWorkforceVocab();
      return data;
    },
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

export function useWorkforceChanges(
  enabled: boolean,
  filter: { status?: WorkforceRecord["status"]; kind?: WorkforceRecord["kind"] } = {},
) {
  return useQuery({
    queryKey: [...queryKeys.workforce, "list", filter.status ?? "", filter.kind ?? ""],
    queryFn: async () => {
      const { data } = await listWorkforceChanges({
        query: { status: filter.status ?? null, kind: filter.kind ?? null },
      });
      return data;
    },
    enabled,
  });
}

export function useWorkforceChange(id: string | null) {
  return useQuery({
    queryKey: queryKeys.workforceChange(id ?? ""),
    queryFn: async () => {
      const { data } = await getWorkforceChange({ path: { wid: id! } });
      return data;
    },
    enabled: !!id,
    // An approved exit or org change hands work to the worker; keep the
    // linked state live while it is in flight.
    refetchInterval: (query) => {
      const r = query.state.data?.record;
      const busy =
        r?.org_scenario_status === "applying" ||
        r?.offboarding_status === "running" ||
        r?.offboarding_status === "scheduled";
      return busy ? 30_000 : false;
    },
  });
}

export function useFutureHires(enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.workforce, "future-hires"],
    queryFn: async () => {
      const { data } = await listFutureHires();
      return data;
    },
    enabled,
  });
}

export type WorkforceSubmission =
  | { kind: "employee_change"; body: EmployeeChangeIn; id?: string }
  | { kind: "planned_exit"; body: PlannedExitIn; id?: string }
  | { kind: "future_hire"; body: FutureHireIn; id?: string };

/** Submit one of the three forms, or resubmit an existing record (`id`). */
export function useSubmitWorkforce() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (s: WorkforceSubmission) => {
      if (s.kind === "employee_change") {
        const { data } = s.id
          ? await updateEmployeeChange({ path: { wid: s.id }, body: s.body })
          : await submitEmployeeChange({ body: s.body });
        return data;
      }
      if (s.kind === "planned_exit") {
        const { data } = s.id
          ? await updatePlannedExit({ path: { wid: s.id }, body: s.body })
          : await submitPlannedExit({ body: s.body });
        return data;
      }
      const { data } = s.id
        ? await updateFutureHire({ path: { wid: s.id }, body: s.body })
        : await submitFutureHire({ body: s.body });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workforce });
    },
  });
}

export type WorkforceDecision = "approve" | "return" | "withdraw";

/** Approve, send back or withdraw. Approving does the work the record implies. */
export function useWorkforceDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      note,
    }: {
      id: string;
      action: WorkforceDecision;
      note?: string;
    }) => {
      if (action === "approve") {
        const { data } = await approveWorkforceChange({
          path: { wid: id },
          body: { note: note ?? null },
        });
        return data;
      }
      if (action === "return") {
        const { data } = await returnWorkforceChange({
          path: { wid: id },
          body: { note: note ?? "" },
        });
        return data;
      }
      const { data } = await withdrawWorkforceChange({
        path: { wid: id },
        body: { note: note ?? null },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workforce });
      queryClient.invalidateQueries({ queryKey: ["offboarding"] });
      queryClient.invalidateQueries({ queryKey: ["org"] });
    },
  });
}

/** The page's audit trail: every action_log row the Workforce page caused. */
export function useWorkforceLog(enabled: boolean, filter: { action?: string; q?: string } = {}) {
  return useQuery({
    queryKey: [...queryKeys.workforce, "log", filter.action ?? "", filter.q ?? ""],
    queryFn: async () => {
      const { data } = await getWorkforceLog({
        query: { action: filter.action || null, q: filter.q || null, limit: 300 },
      });
      return data;
    },
    enabled,
    staleTime: 15 * 1000,
  });
}
